import { useEffect, useState } from "react";
import { Building2, Check, ExternalLink, Globe, Pencil, Plus, ShieldCheck, Star, X } from "lucide-react";

import BrandLogo from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import Loader from "@/components/ui/loader";
import { useToast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";

import OrgAdminsModal from "./OrgAdminsModal";
import OrgFormModal from "./OrgFormModal";
import { PageHero } from "@/components/interior-page";

import { superAdminApi, type Org } from "./session";

/** null = closed, "new" = create, Org = edit that org. */
type ModalState = null | "new" | Org;

export default function SuperAdminOrgsPage() {
  const toast = useToast();
  const [orgs, setOrgs] = useState<Org[] | null>(null);
  const [modal, setModal] = useState<ModalState>(null);
  const [adminsFor, setAdminsFor] = useState<Org | null>(null);

  const load = () =>
    superAdminApi
      .get<Org[]>("/orgs")
      .then(setOrgs)
      .catch((err) => {
        setOrgs([]);
        toast.error(err instanceof Error ? err.message : "Failed to load organizations");
      });

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleActive = async (org: Org) => {
    try {
      await superAdminApi.patch<Org>(`/orgs/${org.id}`, { is_active: !org.is_active });
      void load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to update organization");
    }
  };

  return (
    <div className="px-4 py-8 sm:px-6 lg:px-8">
      <PageHero
        eyebrow="Super Admin"
        icon={Building2}
        title="Organizations"
        description="Each organization is served at its own URL — the default one is the main site, every other is reached at /slug. Deal inventory is shared across all of them."
        actions={
          <Button type="button" onClick={() => setModal("new")}>
            <Plus className="h-4 w-4" />
            New Organization
          </Button>
        }
      />

      <div className="mt-8">
        {orgs === null ? (
          <Loader />
        ) : orgs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No organizations yet.</p>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {orgs.map((org) => (
              <div
                key={org.id}
                className={cn(
                  "rounded-2xl border bg-card/70 p-5",
                  org.is_active ? "border-border/80" : "border-border/50 opacity-60",
                )}
              >
                <div className="flex items-start gap-3">
                  {/* The default org shows the platform wordmark; every other
                      org shows its own uploaded logo, or a placeholder. */}
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-primary/25 bg-primary/10 text-primary">
                    {org.is_default ? (
                      <BrandLogo className="h-7 w-auto p-0.5" />
                    ) : org.logo_url ? (
                      <img src={org.logo_url} alt="" className="h-full w-full object-contain p-1" />
                    ) : (
                      <Building2 className="h-5 w-5" />
                    )}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-base font-bold text-foreground">{org.name}</span>
                      {org.is_default && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-primary/10 px-2 py-0.5 text-[0.58rem] font-bold uppercase tracking-[0.12em] text-primary">
                          <Star className="h-3 w-3" />
                          Default
                        </span>
                      )}
                      {!org.is_active && (
                        <span className="rounded-full border border-border px-2 py-0.5 text-[0.58rem] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                          Inactive
                        </span>
                      )}
                    </div>

                    {/* The default org IS the site — showing it a "/" link
                        would imply it lives at some sub-path. */}
                    {org.is_default ? (
                      <p className="mt-1 text-sm text-muted-foreground">Main site</p>
                    ) : (
                      <a
                        href={org.url_path}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                      >
                        <Globe className="h-3.5 w-3.5" />
                        {org.url_path}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}

                    {org.headline && (
                      <p className="mt-2.5 line-clamp-2 text-sm leading-6 text-muted-foreground">
                        {org.headline}
                      </p>
                    )}

                    {org.tier_names.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {org.tier_names.map((tier) => (
                          <span
                            key={tier}
                            className="rounded-full border border-border/80 bg-muted/50 px-2.5 py-0.5 text-[0.68rem] font-semibold text-foreground/80"
                          >
                            {tier}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setModal(org)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border/80 px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-accent"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => setAdminsFor(org)}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border/80 px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-accent"
                      >
                        <ShieldCheck className="h-3.5 w-3.5" />
                        Admins
                      </button>

                      {/* No deactivate for the default org — taking it offline
                          would take the whole site down. */}
                      {!org.is_default && (
                        <button
                          type="button"
                          onClick={() => toggleActive(org)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border/80 px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-accent"
                        >
                          {org.is_active ? (
                            <>
                              <X className="h-3.5 w-3.5" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <Check className="h-3.5 w-3.5" />
                              Activate
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {adminsFor !== null && (
        <OrgAdminsModal org={adminsFor} onClose={() => setAdminsFor(null)} />
      )}

      {modal !== null && (
        <OrgFormModal
          org={modal === "new" ? null : modal}
          existingSlugs={(orgs ?? []).map((o) => o.slug.toLowerCase())}
          onClose={() => setModal(null)}
          onSaved={(message) => {
            toast.success(message);
            setModal(null);
            void load();
          }}
        />
      )}
    </div>
  );
}
